import { ActionIcon, Badge, Button, Group, Loader, Stack, Text, Tooltip } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Download, Save, Trash2 } from "lucide-react";

import { downloadSignedFile } from "../../api/download";
import {
  getListActorsQueryKey,
  getListJobsQueryKey,
  useDeleteJob,
  useSaveGeneratedActors,
} from "../../api/generated/endpoints/generation/generation";
import type { Catalog, GenerationOutput, Job } from "../../api/generated/model";
import { refreshTeamStorage } from "../../api/team-storage";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { GENERATIONS_PAGE, useJobList } from "../../platform/jobs/job-list";
import { modelName } from "../../platform/jobs/model-name";
import { isSettled } from "../../platform/jobs/status";
import { useStreamStatus } from "../../platform/stream-status";
import { formatDateTime } from "../../ui/format";
import { LoadAhead, morePages } from "../../ui/LoadAhead";
import { MediaCard, MediaExpired, MediaFailed, MediaLoading, MediaPlaceholder } from "../../ui/MediaCard";
import { MediaGrid } from "../../ui/MediaGrid";
import classes from "../../platform/actors/library/actors.module.css";
import { failedWords } from "../../platform/jobs/failed-words";
import { expiredLine, isExpired } from "../../platform/jobs/expired";
import { ThumbImage } from "../../ui/ThumbImage";
import { isSaved, markSaved } from "../../platform/actors/library/saved-portraits";

/** Live's badge for how an actor was asked for (`AVATAR_GENERATION_TYPE`). */
const MODE_LABEL = { prompt: "Prompt", builder: "Builder" } as const;

export interface ActorGenerationsProps {
  teamId: string;
  brandId: string;
  catalog: Catalog | undefined;
}

/**
 * Live's My Generations on the AI Actors page (`tabs/my-generations`, capture L04d, LEFTOVERS
 * 4): the brand's `image.actor` jobs, newest first, kept live by the stream. Each row says how
 * it was asked for ("Prompt" or "Builder"), when and on which model, and offers live's "Save
 * generation" (every delivered portrait as the team's actors), "Download generation" and "Delete
 * generation" (at once, as live's; not while it runs); each portrait has live's Save, then
 * "Saved" on every visit while its actor lasts (issue 407), and Download. With none yet, live's
 * four placeholders.
 */
export function ActorGenerations({ teamId, brandId, catalog }: ActorGenerationsProps) {
  const jobs = useJobList(
    teamId,
    { status: "all", brandId, types: ["image.actor"], pageSize: GENERATIONS_PAGE },
    useStreamStatus(),
  );
  const items = jobs.data?.pages.flatMap((page) => page.items) ?? [];
  if (jobs.isPending) return <Loader size="sm" aria-label="Loading your generations" />;
  if (jobs.isError) return <ErrorAlert error={jobs.error} />;
  if (items.length === 0 && !jobs.hasNextPage) {
    // Live's empty My Generations: its placeholder tiles (capture L04d).
    return (
      <MediaGrid label="Your next actors">
        {Array.from({ length: 4 }, (_, at) => (
          <MediaPlaceholder key={at} aspectRatio="1:1" label="New actor" />
        ))}
      </MediaGrid>
    );
  }
  return (
    <Stack gap={24} aria-label="My Generations">
      {items.map((job) => (
        <ActorRow
          key={job.id}
          teamId={teamId}
          brandId={brandId}
          job={job}
          model={job.model === null ? "" : modelName(catalog, job.model)}
          readAgain={() => void jobs.refetch()}
        />
      ))}
      {/* Live's: the next page as the end shows (`InfiniteScroll`), no button. */}
      <LoadAhead more={morePages(jobs)} loaded={items.length} label="Loading more generations" />
    </Stack>
  );
}

interface ActorRowProps {
  teamId: string;
  brandId: string;
  job: Job;
  model: string;
  readAgain: () => void;
}

/** A delivered portrait, with its picture. */
const delivered = (output: GenerationOutput) => output.status === "delivered" && output.url !== null;

/** One `image.actor` job: live's header and actions, then its portraits. */
function ActorRow({ teamId, brandId, job, model, readAgain }: ActorRowProps) {
  const queryClient = useQueryClient();
  // Live's "Delete generation" (#237, LEFTOVERS 4): only its maker may, so anyone else sees why.
  const remove = useDeleteJob({
    mutation: {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) }),
    },
  });
  // Live marks a saved portrait "Saved" (the route keeps each once), as the job's next read will.
  const save = useSaveGeneratedActors({
    mutation: {
      onSuccess: async (made, { data }) => {
        markSaved(queryClient, teamId, data.job_id, data.output_indexes, made.items);
        // Each saved portrait counts in the team's storage.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [getListActorsQueryKey(teamId, brandId)[0]] }),
          refreshTeamStorage(queryClient, teamId),
        ]);
      },
    },
  });
  const ready = job.outputs.filter(delivered);
  const unsaved = ready.filter((output) => !isSaved(output));
  const saveSome = (indexes: number[]) =>
    save.mutate({ teamId, brandId, data: { job_id: job.id, output_indexes: indexes } });
  // The portrait's own type names the file's extension ("image/png" is ".png").
  const fileOf = (output: GenerationOutput) =>
    `avatar-${job.id}-${output.index + 1}.${output.mime?.split("/")[1] ?? "png"}`;
  const mode = job.params?.actor?.mode ?? "prompt";
  const when = formatDateTime(job.created_at);
  return (
    <article className={classes.generation} aria-label={`${MODE_LABEL[mode]}, ${when}`}>
      <Group justify="space-between" wrap="nowrap" mb={8}>
        <Group gap={8} wrap="nowrap" miw={0}>
          <Badge variant="light" size="sm">
            {MODE_LABEL[mode]}
          </Badge>
          <Text size="xs" c="var(--cai-color-text-muted)" truncate>
            {[when, model].filter(Boolean).join(" · ")}
          </Text>
        </Group>
        <Group gap={4} wrap="nowrap">
          {job.status === "succeeded" && unsaved.length > 0 ? (
            <Tooltip label="Save generation">
              <ActionIcon
                variant="subtle"
                aria-label="Save generation"
                loading={save.isPending}
                onClick={() => saveSome(unsaved.map((output) => output.index))}
              >
                <Save size={18} />
              </ActionIcon>
            </Tooltip>
          ) : null}
          <Tooltip label="Download generation">
            <ActionIcon
              variant="subtle"
              aria-label="Download generation"
              disabled={ready.length === 0}
              onClick={() =>
                void (async () => {
                  for (const output of ready) {
                    await downloadSignedFile(output.url!, fileOf(output)).catch(() => undefined);
                  }
                })()
              }
            >
              <Download size={18} />
            </ActionIcon>
          </Tooltip>
          {/* Live's delete: at once, no question, and not while the generation runs. */}
          <Tooltip label="Delete generation">
            <ActionIcon
              variant="subtle"
              aria-label="Delete generation"
              disabled={!isSettled(job.status)}
              loading={remove.isPending}
              onClick={() => remove.mutate({ teamId, jobId: job.id })}
            >
              <Trash2 size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      {save.isError ? <ErrorAlert error={save.error} /> : null}
      {remove.isError ? <ErrorAlert error={remove.error} /> : null}
      <MediaGrid>
        {job.error === null ? null : (
          <MediaFailed {...failedWords(job.type, job.error.message)} />
        )}
        {job.error !== null
          ? null
          : job.outputs.map((output) =>
              delivered(output) ? (
                <MediaCard
                  key={output.index}
                  saved={isSaved(output)}
                  menu={
                    <Group gap={4} wrap="nowrap">
                      {isSaved(output) ? (
                        <Button
                          variant="text-white"
                          size="compact-sm"
                          leftSection={<Check size={16} />}
                          disabled
                        >
                          Saved
                        </Button>
                      ) : (
                        <Button
                          variant="text-white"
                          size="compact-sm"
                          leftSection={<Save size={16} />}
                          onClick={() => saveSome([output.index])}
                        >
                          Save
                        </Button>
                      )}
                      <Tooltip label="Download">
                        <ActionIcon
                          variant="text-white"
                          aria-label={`Download portrait ${output.index + 1}`}
                          onClick={() =>
                            void downloadSignedFile(output.url!, fileOf(output)).catch(() => undefined)
                          }
                        >
                          <Download size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  }
                >
                  <ThumbImage
                    // The 512 px thumbnail, else the portrait itself (issue 828, item 12).
                    thumb={output.thumb_url}
                    full={output.url!}
                    alt={`Portrait ${output.index + 1}`}
                    className={classes.portraitImage}
                    onLost={readAgain}
                  />
                </MediaCard>
              ) : isExpired(output) ? (
                <MediaExpired key={output.index} title={expiredLine(job)} />
              ) : (
                output.status === "failed" ? (
                  <MediaPlaceholder key={output.index} aspectRatio="2:3" label={`Portrait ${output.index + 1}, failed`} />
                ) : (
                  // Live's loader while it is made (spec ui-perf/ui-switch-classic-simple-pro, decision 19).
                  <MediaLoading key={output.index} aspectRatio="2:3" label={`Portrait ${output.index + 1}, waiting`} />
                )
              ),
            )}
      </MediaGrid>
    </article>
  );
}
